import moment from "moment-timezone";
import {
  CONTRACTS_REVIEW_PENDING,
  DASHBOARD_FEATURES,
  DATE_FORMAT,
  defaultStateOfVmoBot,
} from "../constants";
import {
  ClauseCheckModel,
  ContractDocument,
  ContractReviewModel,
  TTCDocumentRequestModalForLocalUpload,
} from "../model";
import {
  ClauseCheckType,
  ContractReviewType,
  IContractExpiryDashboard,
  vmoBotType,
} from "../types";
import {
  VMO_BOT_ACTIONS,
  CONTRACT_REVIEW_ACTIONS,
  CLAUSE_CHECK_ACTIONS,
  CONTRACT_EXPIRY_DASHBOARD_ACTIONS,
  ADK_TTC_BOT_ACTIONS,
} from "./dashboard.thunk";
import { addDaysToCurrentDate } from "../../../components/AntdTable/AntdTable.utils";
import { DASHBOARD_ENTITIES } from "../../../common/constants";

interface vscDashboardSliceProps {
  contractsReview: ContractReviewType;
  clauseCheck: ClauseCheckType;
  clauseCheckAdvanced: {
    headerText: string;
  };
  vmoBot: vmoBotType;
  contractExpiryDashboard: IContractExpiryDashboard;
  adkTTCBot: {
    apps: any[];
    sessionId: string | null;
    chat: any[];
    view: "reference" | "history" | "chat";
    verticalChanged: boolean;
    selectedModel: string;
    corpusMappings: Record<string, string>;
    // A0.2 — map of vertical_id → session_id. Lets us keep a dedicated session
    // per vertical, so switching verticals no longer leaks earlier vertical's
    // context into the new conversation. Hydrated from localStorage on mount;
    // re-persisted on every update so reloads stay coherent.
    verticalSessions: Record<string, string>;
    // TTC filter (active/expired/all). Stored per-vertical to match A0.2's
    // session scoping — an "expired" filter on vertical A shouldn't carry
    // over to vertical B. Defaults to "active" for any vertical not yet in
    // the map. Hydrated from localStorage on mount.
    verticalStatusFilters: Record<string, "active" | "expired" | "all">;
    processingStage:
      | "idle"
      | "loading"
      | "SelectFiles"
      | "UploadingFiles"
      | "ProcessingFiles"
      | "initializing"
      | null;
  };
}

const initialState: vscDashboardSliceProps = {
  contractsReview: new ContractReviewModel({}),
  clauseCheck: new ClauseCheckModel({}),
  clauseCheckAdvanced: {
    headerText: DASHBOARD_FEATURES.CLAUSE_CHECK_ADVANCED,
  },
  vmoBot: defaultStateOfVmoBot,
  contractExpiryDashboard: {
    searchText: "",
    dates: {
      from_date: moment().format(DATE_FORMAT),
      to_date: addDaysToCurrentDate(30),
    },
    vendor_name: [],
    graphData: [],
    selectedFilterIdx: 0,
  },
  adkTTCBot: {
    apps: [],
    sessionId: null,
    chat: [],
    view: "chat",
    verticalChanged: false,
    selectedModel: "gemini-2.0-flash-001",
    processingStage: "idle",
    corpusMappings: {},
    // A0.2 — hydrated lazily in ADKBasedTTC on mount from localStorage.
    // Stored under key "ttc_vertical_sessions_<user_id>".
    verticalSessions: {},
    // Stored under key "ttc_vertical_status_filters_<user_id>".
    verticalStatusFilters: {},
  },
};

export default function vscDashboardReducer(state = initialState, action: any) {
  switch (action.type) {
    case CONTRACT_REVIEW_ACTIONS.ADD_DOCUMENT:
      var newList = state.contractsReview.pending_documents.list.slice();
      newList.unshift(action.payload);
      state = {
        ...state,
        contractsReview: {
          ...state.contractsReview,
          pending_documents: {
            ...state.contractsReview.pending_documents,
            list: newList,
          },
        },
      };
      return state;

    case CONTRACT_REVIEW_ACTIONS.UPDATE_DETAILS_OF_UPLOADING_DOCUMENT:
      newList = state.contractsReview.pending_documents.list.slice();
      newList[0] = action.payload;
      state = {
        ...state,
        contractsReview: {
          ...state.contractsReview,
          pending_documents: {
            ...state.contractsReview.pending_documents,
            list: newList,
          },
        },
      };
      return state;

    case CONTRACT_REVIEW_ACTIONS.UPDATE_FILE_UPLOAD_STATUS:
      var fileUpload = {
        ...state.contractsReview.pending_documents.fileUpload,
        ...action.payload,
      };
      state = {
        ...state,
        contractsReview: {
          ...state.contractsReview,
          pending_documents: {
            ...state.contractsReview.pending_documents,
            fileUpload: fileUpload,
          },
        },
      };
      return state;

    case CONTRACT_REVIEW_ACTIONS.REMOVE_DOCUMENT:
      newList = state.contractsReview.pending_documents.list.slice();
      newList.shift();
      state = {
        ...state,
        contractsReview: {
          ...state.contractsReview,
          pending_documents: {
            ...state.contractsReview.pending_documents,
            list: newList,
          },
        },
      };
      return state;

    case CONTRACT_REVIEW_ACTIONS.SET_CONTRACT_REVIEW_DATA:
      let contractsReview = JSON.parse(JSON.stringify(state.contractsReview));
      contractsReview = {
        ...contractsReview,
        ...action.payload,
      };
      state = {
        ...state,
        contractsReview: contractsReview,
      };
      return state;

    case CONTRACT_REVIEW_ACTIONS.UPDATE_PENDING_DOC_COUNT:
      var userDocsCopy = state.contractsReview.user_docs.slice();
      var docObjectIdx = userDocsCopy.findIndex((doc) => {
        return doc.text === CONTRACTS_REVIEW_PENDING;
      });
      if (docObjectIdx > -1) {
        userDocsCopy[docObjectIdx] = {
          ...userDocsCopy[docObjectIdx],
          count: userDocsCopy[docObjectIdx].count + 1,
        };
      }
      state = {
        ...state,
        contractsReview: {
          ...state.contractsReview,
          user_docs: userDocsCopy,
        },
      };
      return state;

    case VMO_BOT_ACTIONS.UPDATE_VMO_BOT_DATA:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          ...action.payload,
        },
      };
      return state;

    case VMO_BOT_ACTIONS.ADD_MESSAGE_TO_CHAT:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          chat: state.vmoBot.chat.concat(action.payload || {}),
        },
      };
      return state;

    case VMO_BOT_ACTIONS.UPDATE_MESSAGE_DATA:
      const chatCopy = state.vmoBot.chat.slice();

      const messageIdx = chatCopy.findIndex((msg) => {
        return msg.id === action.payload.id;
      });

      if (messageIdx > -1) {
        chatCopy[messageIdx] = { ...chatCopy[messageIdx], ...action.payload };
      }

      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          chat: chatCopy,
        },
      };

      return state;

    case VMO_BOT_ACTIONS.REMOVE_SUGGESTION:
      const suggestionsCopy = state.vmoBot.suggestions.slice();
      const suggestionIdx = suggestionsCopy.findIndex((suggestion) => {
        return suggestion.text === action.payload.text;
      });
      if (suggestionIdx > -1) {
        suggestionsCopy.splice(suggestionIdx, 1);
      }
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          suggestions: suggestionsCopy,
        },
      };
      return state;

    case VMO_BOT_ACTIONS.UPLOAD_VMO_FILES:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          fileUpload: {
            ...state.vmoBot.fileUpload,
            uploadedFiles: action.payload,
          },
        },
      };

      return state;

    case VMO_BOT_ACTIONS.UPDATE_UPLOAD_STATUS:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          fileUpload: {
            ...state.vmoBot.fileUpload,
            uploadingState: action.payload,
          },
        },
      };

      return state;

    case VMO_BOT_ACTIONS.UPDATE_PROCESSING_STAGE:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          fileUpload: {
            ...state.vmoBot.fileUpload,
            processingStage: action.payload,
          },
        },
      };

      return state;

    case VMO_BOT_ACTIONS.UPDATE_PROCESSED_FILES:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          fileUpload: {
            ...state.vmoBot.fileUpload,
            processedFiles: {
              ...state.vmoBot.fileUpload?.processedFiles,
              [DASHBOARD_ENTITIES.TALK_TO_CONTRACT]:
                state.vmoBot.fileUpload?.processedFiles?.[
                  DASHBOARD_ENTITIES.TALK_TO_CONTRACT
                ]?.concat(action.payload),
            },
          },
        },
      };
      return state;

    case VMO_BOT_ACTIONS.SET_SUGGESTIONS:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          suggestions: action.payload,
        },
      };
      return state;

    case VMO_BOT_ACTIONS.SET_MESSAGE_LOADER:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          isMessageLoading: action.payload,
        },
      };
      return state;

    case VMO_BOT_ACTIONS.RESET_VMO_BOT_CHAT:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          ...defaultStateOfVmoBot,
          ...action.payload,
        },
      };
      return state;

    case VMO_BOT_ACTIONS.SET_SELECTED_MODEL:
      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          selectedModel: action.payload,
        },
      };
      return state;

    case VMO_BOT_ACTIONS.UPDATE_LAST_SYSTEM_MESSAGE:
      const chatCopyForUpdate = state.vmoBot.chat.slice() as any;
      const chatMessageIdx = action.payload.chatMessageIdx;
      const message = chatCopyForUpdate[chatMessageIdx];
      const text = message?.text || [];
      const systemMessageId = action.payload.id;

      if (systemMessageId) {
        chatCopyForUpdate[chatMessageIdx] = {
          ...message,
          text: action.payload.data?.map(
            (fileData: any) =>
              new TTCDocumentRequestModalForLocalUpload(fileData)
          ),
        };
      } else {
        chatCopyForUpdate[chatMessageIdx] = {
          ...message,
          text: Array.isArray(text)
            ? text?.map((fileData: any) => {
                const getDocData = action.payload.data?.find(
                  (doc: any) => doc.file_id === fileData?.id
                );
                return {
                  ...fileData,
                  id: getDocData?.id,
                };
              })
            : text,
        };
      }

      state = {
        ...state,
        vmoBot: {
          ...state.vmoBot,
          chat: chatCopyForUpdate,
        },
      };
      return state;

    case CLAUSE_CHECK_ACTIONS.ADD_DOCUMENT_FOR_CLAUSE_CHECK:
      var listCopyForClauseCheck =
        state.clauseCheck.pending_documents.list.slice();
      listCopyForClauseCheck.unshift(action.payload);
      state = {
        ...state,
        clauseCheck: {
          ...state.clauseCheck,
          pending_documents: {
            ...state.clauseCheck.pending_documents,
            list: listCopyForClauseCheck,
          },
        },
      };
      return state;

    case CLAUSE_CHECK_ACTIONS.UPDATE_DETAILS_OF_UPLOADING_DOCUMENT_FOR_CLAUSE_CHECK_AFTER_SUCCESSFUL_UPLOAD:
      listCopyForClauseCheck = state.clauseCheck.pending_documents.list.slice();
      docObjectIdx = state.clauseCheck.pending_documents.list.findIndex(
        (doc) => doc.file_id === action.payload.file_id
      );
      if (docObjectIdx > -1) {
        listCopyForClauseCheck[docObjectIdx] = {
          ...listCopyForClauseCheck[docObjectIdx],
          ...new ContractDocument(action.payload.doc),
          loading: true,
        };
      }
      state = {
        ...state,
        clauseCheck: {
          ...state.clauseCheck,
          pending_documents: {
            ...state.clauseCheck.pending_documents,
            list: listCopyForClauseCheck,
          },
        },
      };
      return state;

    case CLAUSE_CHECK_ACTIONS.UPDATE_DATA_FOR_CLAUSE_CHECK_DOCUMENT:
      listCopyForClauseCheck = state.clauseCheck.pending_documents.list.slice();
      docObjectIdx = state.clauseCheck.pending_documents.list.findIndex(
        (doc) => doc.file_id === action.payload.file_id
      );
      if (docObjectIdx > -1) {
        listCopyForClauseCheck[docObjectIdx] = {
          ...listCopyForClauseCheck[docObjectIdx],
          ...action.payload.data,
        };
      }
      state = {
        ...state,
        clauseCheck: {
          ...state.clauseCheck,
          pending_documents: {
            ...state.clauseCheck.pending_documents,
            list: listCopyForClauseCheck,
          },
        },
      };
      return state;

    case CLAUSE_CHECK_ACTIONS.SET_CLAUSE_CHECK_DATA:
      state = {
        ...state,
        clauseCheck: {
          ...state.clauseCheck,
          ...action.payload,
        },
      };
      return state;

    case CLAUSE_CHECK_ACTIONS.REMOVE_DOCUMENT_FROM_CLAUSE_CHECK:
      state = {
        ...state,
        clauseCheck: {
          ...state.clauseCheck,
          pending_documents: {
            ...state.clauseCheck.pending_documents,
            list: [],
          },
        },
      };

      return state;

    case CLAUSE_CHECK_ACTIONS.SET_FAILED_DOC_DATA:
      state = {
        ...state,
        clauseCheck: {
          ...state.clauseCheck,
          failed_documents: action.payload,
        },
      };
      return state;

    case CONTRACT_EXPIRY_DASHBOARD_ACTIONS.SET_CONTRACT_EXPIRY_DASHBOARD_DATA:
      state = {
        ...state,
        contractExpiryDashboard: {
          ...state.contractExpiryDashboard,
          ...action.payload,
        },
      };
      return state;

    case CONTRACT_EXPIRY_DASHBOARD_ACTIONS.SET_CONTRACT_EXPIRY_DASHBOARD_GRAPH_DATA:
      state = {
        ...state,
        contractExpiryDashboard: {
          ...state.contractExpiryDashboard,
          graphData: action.payload,
        },
      };
      return state;

    case ADK_TTC_BOT_ACTIONS.SET_ADK_TTC_BOT_DATA:
      state = {
        ...state,
        adkTTCBot: {
          ...state.adkTTCBot,
          ...action.payload,
        },
      };
      return state;

    case ADK_TTC_BOT_ACTIONS.RESET_ADK_CHAT_BOT:
      state = {
        ...state,
        adkTTCBot: {
          ...state.adkTTCBot,
          ...{ sessionId: null, chat: [] },
          ...action.payload,
        },
      };

      return state;

    default:
      return state;
  }
}
